import * as React from "react";
import {
  GridColDef,
  GridRowsProp,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { useTitle } from "../../hooks/useTitle";
import { useGetManualDepositsQuery } from "../../services/finance.service";
import dayjs from "dayjs";
// import ModalManualDepositForm from "./ModalManualDepositForm";
import { ManualDeposit } from "../../types/finance";
import { formatMoney } from "../../utils/helper";
import { CardBase, GlassDataGrid, Button, OrangeLinearProgress } from "../../components/global";
import { Download as DownloadIcon } from "@mui/icons-material";

const ManualDepositsPage = () => {
  useTitle("Manual Deposits");
  // const navigate = useNavigate();
  // const user = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);

  const { data, isLoading, isFetching } = useGetManualDepositsQuery({
    page,
    pageSize,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        minWidth: isMobile ? 120 : 200,
        sortingOrder: ["desc", "asc", null],
        sortable: true,
        filterable: false,
        valueGetter: (
          params: GridValueGetterParams<undefined, ManualDeposit>
        ) => `${dayjs(params.row?.created_at).format("MMM DD, YYYY hh:mm A")}`,
      },
      {
        field: "company.name",
        headerName: "Company Name",
        minWidth: isMobile ? 150 : 200,
        sortable: true,
        filterable: true,
        valueGetter: (
          params: GridValueGetterParams<undefined, ManualDeposit>
        ) => `${params.row?.company?.name}`,
      },
      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: isMobile ? 150 : 200,
        sortable: false,
        filterable: false,
      },
      {
        field: "reason.name",
        headerName: "Reason",
        flex: 1,
        minWidth: isMobile ? 120 : 150,
        sortable: false,
        filterable: false,
        valueGetter: (
          params: GridValueGetterParams<undefined, ManualDeposit>
        ) => `${params.row?.reason?.name}`,
      },
      {
        field: "amount",
        headerName: "Amount",
        type: "number",
        minWidth: isMobile ? 100 : 200,
        sortable: false,
        filterable: false,
        valueGetter: (
          params: GridValueGetterParams<undefined, ManualDeposit>
        ) => `${formatMoney(params.row?.amount || 0)}`,
      },
    ],
    [isMobile]
  );

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns
    const visibleColumns = columns.filter((col) => !col.hide);

    // Create CSV headers
    const headers = visibleColumns.map((col) => `"${col.headerName}"`).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns
        .map((col) => {
          let value: string = "";
          if (col.valueGetter) {
            const params = { row } as any;
            const result = col.valueGetter(params);
            value = result != null ? String(result) : "";
          } else if (col.renderCell) {
            // For renderCell, try to get raw value
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          } else {
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          }
          // Escape quotes and wrap in quotes
          if (value && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Manual_Deposits_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <Box>
      <CardBase
        title="Manual Deposits"
        useGlassCard={false}
        sx={{ height: "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems="stretch"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!rows || rows.length === 0}
              size="small"
              fullWidth={isMobile}
              sx={{
                borderColor: "#F4A259",
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                  borderColor: "#F4A259",
                },
                "&:disabled": {
                  borderColor: "rgba(244, 162, 89, 0.5)",
                  color: "rgba(244, 162, 89, 0.5)",
                },
              }}
            >
              {isMobile ? "Export" : "Export CSV"}
            </Button>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          height={isMobile ? "calc(85vh - 120px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              py: isMobile ? 1 : 2,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
    </Box>
  );
};
export default ManualDepositsPage;
